import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios'
import { getAuth } from 'firebase/auth'
import { ApiResponse, PaginatedResponse } from '@/types'

class ApiService {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      async (config) => {
        // In development mode, skip Firebase auth for API calls
        if (import.meta.env.DEV) {
          // Check if user is logged in via localStorage
          const storedUser = localStorage.getItem('cosmk_dev_user')
          if (storedUser) {
            // Add a dev auth header (backend can validate this differently)
            config.headers['X-Dev-Auth'] = 'true'
            try {
              const userData = JSON.parse(storedUser)
              config.headers['X-Dev-User'] = JSON.stringify(userData.user)
            } catch (error) {
              console.error('Error parsing stored user data:', error)
            }
          }
          return config
        }

        // Production Firebase auth
        const auth = getAuth()
        const user = auth.currentUser
        
        if (user) {
          const token = await user.getIdToken()
          config.headers.Authorization = `Bearer ${token}`
        }
        
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // In development mode, don't redirect to login to avoid loops
          if (!import.meta.env.DEV) {
            // Handle unauthorized access in production
            window.location.href = '/login'
          } else {
            console.warn('API 401 error in development mode:', error.config?.url)
          }
        }
        return Promise.reject(error)
      }
    )
  }

  // Generic request methods
  async get<T>(url: string, params?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.get(url, { params })
      return response.data
    } catch (error) {
      return this.handleError(error)
    }
  }

  async post<T>(url: string, data?: any, options?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.post(url, data, options)
      return response.data
    } catch (error) {
      return this.handleError(error)
    }
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.put(url, data)
      return response.data
    } catch (error) {
      return this.handleError(error)
    }
  }

  async patch<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.patch(url, data)
      return response.data
    } catch (error) {
      return this.handleError(error)
    }
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.delete(url)
      return response.data
    } catch (error) {
      return this.handleError(error)
    }
  }

  async upload<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<T>> {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await this.api.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            onProgress(progress)
          }
        },
      })
      return response.data
    } catch (error) {
      return this.handleError(error)
    }
  }

  // Error handling
  private handleError(error: any): ApiResponse {
    console.error('API Error:', error)
    
    if (error.response) {
      // Server responded with error status
      const status = error.response.status
      let message = error.response.data?.message || 'An error occurred'
      
      switch (status) {
        case 401:
          message = 'Authentication required. Please log in again.'
          break
        case 403:
          message = 'Access denied. You do not have permission to perform this action.'
          break
        case 404:
          message = 'Resource not found.'
          break
        case 413:
          message = 'File too large. Please choose a smaller file.'
          break
        case 429:
          message = 'Too many requests. Please try again later.'
          break
        case 500:
          message = 'Server error. Please try again later.'
          break
        default:
          message = `Server error (${status}): ${message}`
      }
      
      return {
        success: false,
        error: message,
        data: error.response.data
      }
    } else if (error.request) {
      // Network error - no response received
      console.warn('Network error - no response received:', error.request)
      return {
        success: false,
        error: 'Network error. Please check your connection and try again.'
      }
    } else if (error.code === 'ECONNABORTED') {
      // Request timeout
      return {
        success: false,
        error: 'Request timed out. Please try again.'
      }
    } else {
      // Other errors
      return {
        success: false,
        error: error.message || 'An unexpected error occurred'
      }
    }
  }
}

// Content API methods
export const contentApi = {
  // Get content items
  getContent: (params?: any) => apiService.get<PaginatedResponse<any>>('/content', params),
  
  // Get single content item
  getContentById: (id: string) => apiService.get<any>(`/content/${id}`),
  
  // Upload content
  uploadContent: (file: File, _metadata: any, onProgress?: (progress: number) => void) =>
    apiService.upload<any>('/upload/content', file, onProgress),
  
  // Update content
  updateContent: (id: string, data: any) => apiService.put<any>(`/content/${id}`, data),
  
  // Delete content
  deleteContent: (id: string) => apiService.delete<any>(`/content/${id}`),
  
  // Get content analytics
  getAnalytics: (id: string, params?: any) => apiService.get<any>(`/content/${id}/analytics`, params),
}

// User API methods
export const userApi = {
  // Get current user
  getCurrentUser: () => apiService.get<any>('/auth/me'),
  
  // Update user profile
  updateProfile: (data: any) => apiService.put<any>('/auth/profile', data),
  
  // Get user content
  getUserContent: (params?: any) => apiService.get<PaginatedResponse<any>>('/user/content', params),
}

// Tenant API methods
export const tenantApi = {
  // Get tenant info
  getTenant: (id: string) => apiService.get<any>(`/tenants/${id}`),
  
  // Update tenant config
  updateTenant: (id: string, data: any) => apiService.put<any>(`/tenants/${id}`, data),
  
  // Get tenant users
  getTenantUsers: (id: string, params?: any) => apiService.get<PaginatedResponse<any>>(`/tenants/${id}/users`, params),
}

// Analytics API methods
export const analyticsApi = {
  // Get platform analytics
  getPlatformAnalytics: (params?: any) => apiService.get<any>('/analytics/platform', params),
  
  // Get tenant analytics
  getTenantAnalytics: (tenantId: string, params?: any) => apiService.get<any>(`/analytics/tenant/${tenantId}`, params),
  
  // Get content analytics
  getContentAnalytics: (contentId: string, params?: any) => apiService.get<any>(`/analytics/content/${contentId}`, params),
  
  // Track event
  trackEvent: (data: any) => apiService.post<any>('/analytics/track', data),
}

// Blockchain API methods
export const blockchainApi = {
  // Mint NFT
  mintNFT: (contentId: string, metadata: any) => apiService.post<any>(`/blockchain/mint/${contentId}`, metadata),
  
  // Get NFT info
  getNFT: (tokenId: string) => apiService.get<any>(`/blockchain/nft/${tokenId}`),
  
  // Get user NFTs
  getUserNFTs: (params?: any) => apiService.get<PaginatedResponse<any>>('/blockchain/nfts', params),
}

// Compression API methods (Backend Proxy - Used as fallback)
// NOTE: Primary compression now uses external service directly via compressionService.ts
export const compressionApi = {
  // Compress a file via backend proxy
  compressFile: async (file: File, options: { quality?: 'LOW' | 'MEDIUM' | 'HIGH', preserveMetadata?: boolean } = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    if (options.quality) formData.append('quality', options.quality)
    if (options.preserveMetadata !== undefined) formData.append('preserveMetadata', String(options.preserveMetadata))
    
    try {
      const response = await apiService.post('/compression/compress', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data
    } catch (error) {
      return apiService['handleError'](error)
    }
  },
  
  // Get compression job status via backend proxy
  getStatus: (jobId: string) => apiService.get<any>(`/compression/status/${jobId}`),
  
  // Download compressed file via backend proxy
  downloadFile: async (jobId: string) => {
    try {
      const response = await apiService.get(`/compression/download/${jobId}`, {
        responseType: 'blob',
        timeout: 120000 // 2 minutes for large file downloads
      })
      return response.data
    } catch (error) {
      throw apiService['handleError'](error)
    }
  },
  
  // Get compression service health via backend proxy
  getHealth: () => apiService.get<any>('/compression/health'),
  
  // Get compression statistics via backend proxy (admin only)
  getStats: () => apiService.get<any>('/compression/stats'),
}

// Create singleton instance
const apiService = new ApiService()

export default apiService 